import { format } from 'date-fns';
import type { PlayEvent, PlayEventMatch, PlayEventParticipant, PlayEventRound, PlayEventTeam } from '../types/playEvent';
import { getPlayEventParticipantDisplayName, type PlayEventDisplayParticipant } from './playEventParticipant';
import { resolveRoundStartTime } from './playEventVisibility';

type ParticipantNameLookup = Parameters<typeof getPlayEventParticipantDisplayName>[0];

type PlayEventMatchSortKey = Pick<PlayEventMatch, 'id' | 'createdAt' | 'roundNumber' | 'matchIndex'>;

/** Stable operator-draw order: round, match index, then legacy created_at/id tie-breakers. */
export function comparePlayEventMatchesForDisplay(
  left: PlayEventMatchSortKey,
  right: PlayEventMatchSortKey
): number {
  if (left.roundNumber !== right.roundNumber) {
    return left.roundNumber - right.roundNumber;
  }
  const leftIndex = left.matchIndex;
  const rightIndex = right.matchIndex;
  if (
    typeof leftIndex === 'number' &&
    typeof rightIndex === 'number' &&
    leftIndex !== rightIndex
  ) {
    return leftIndex - rightIndex;
  }
  const leftCreated = left.createdAt ? new Date(left.createdAt).getTime() : 0;
  const rightCreated = right.createdAt ? new Date(right.createdAt).getTime() : 0;
  if (leftCreated !== rightCreated) {
    return leftCreated - rightCreated;
  }
  return left.id.localeCompare(right.id);
}

export function sortPlayEventMatchesForDisplay<T extends PlayEventMatchSortKey>(
  matches: T[]
): T[] {
  return [...matches].sort(comparePlayEventMatchesForDisplay);
}

export function collectParticipantIdsFromMatch(
  match: Pick<
    PlayEventMatch,
    'sideAParticipantId' | 'sideBParticipantId' | 'sideATeamId' | 'sideBTeamId'
  >,
  teamsById: Map<string, Pick<PlayEventTeam, 'participantAId' | 'participantBId'>>
): Set<string> {
  const ids = new Set<string>();
  if (match.sideAParticipantId) ids.add(match.sideAParticipantId);
  if (match.sideBParticipantId) ids.add(match.sideBParticipantId);

  if (match.sideATeamId) {
    const team = teamsById.get(match.sideATeamId);
    if (team) {
      ids.add(team.participantAId);
      ids.add(team.participantBId);
    }
  }
  if (match.sideBTeamId) {
    const team = teamsById.get(match.sideBTeamId);
    if (team) {
      ids.add(team.participantAId);
      ids.add(team.participantBId);
    }
  }

  return ids;
}

export function getSittingOutParticipantsForRound(
  roundNumber: number,
  rosterParticipants: PlayEventParticipant[],
  matches: PlayEventMatch[],
  teamsById: Map<string, Pick<PlayEventTeam, 'participantAId' | 'participantBId'>>,
  withdrawnParticipantIds: string[] = []
): PlayEventParticipant[] {
  const withdrawn = new Set(withdrawnParticipantIds);
  const playing = new Set<string>();

  for (const match of matches) {
    if (match.roundNumber !== roundNumber) continue;
    for (const id of collectParticipantIdsFromMatch(match, teamsById)) {
      playing.add(id);
    }
  }

  return rosterParticipants.filter(
    (participant) => !playing.has(participant.id) && !withdrawn.has(participant.id)
  );
}

export function formatPlayEventParticipantNames(
  participants: ParticipantNameLookup[],
  emptyLabel = 'None'
): string {
  if (participants.length === 0) return emptyLabel;
  return participants.map((participant) => getPlayEventParticipantDisplayName(participant)).join(', ');
}

export function formatPlayEventTeamLabel(
  team: Pick<PlayEventTeam, 'participantAId' | 'participantBId'>,
  participantsById: Map<string, ParticipantNameLookup>
): string {
  const participantA = participantsById.get(team.participantAId);
  const participantB = participantsById.get(team.participantBId);
  const nameA = participantA ? getPlayEventParticipantDisplayName(participantA) : 'TBD';
  const nameB = participantB ? getPlayEventParticipantDisplayName(participantB) : 'TBD';
  return `${nameA} & ${nameB}`;
}

function getPlayEventSidePlayerNames(
  participantId: string | null | undefined,
  teamId: string | null | undefined,
  participantsById: Map<string, ParticipantNameLookup>,
  teamsById: Map<string, PlayEventTeam>
): string[] {
  if (teamId) {
    const team = teamsById.get(teamId);
    if (!team) return ['TBD'];
    const participantA = participantsById.get(team.participantAId);
    const participantB = participantsById.get(team.participantBId);
    return [
      participantA ? getPlayEventParticipantDisplayName(participantA) : 'TBD',
      participantB ? getPlayEventParticipantDisplayName(participantB) : 'TBD',
    ];
  }

  if (participantId) {
    const participant = participantsById.get(participantId);
    return [participant ? getPlayEventParticipantDisplayName(participant) : 'TBD'];
  }

  return ['TBD'];
}

function getPlayEventSidePlayerParticipants<T extends PlayEventDisplayParticipant>(
  participantId: string | null | undefined,
  teamId: string | null | undefined,
  participantsById: Map<string, T>,
  teamsById: Map<string, PlayEventTeam>
): T[] {
  if (teamId) {
    const team = teamsById.get(teamId);
    if (!team) return [];
    const participants: T[] = [];
    const participantA = participantsById.get(team.participantAId);
    const participantB = participantsById.get(team.participantBId);
    if (participantA) participants.push(participantA);
    if (participantB) participants.push(participantB);
    return participants;
  }

  if (participantId) {
    const participant = participantsById.get(participantId);
    return participant ? [participant] : [];
  }

  return [];
}

export function getPlayEventMatchSideParticipants<T extends PlayEventDisplayParticipant>(
  match: Pick<
    PlayEventMatch,
    | 'result'
    | 'sideAParticipantId'
    | 'sideBParticipantId'
    | 'sideATeamId'
    | 'sideBTeamId'
  >,
  participantsById: Map<string, T>,
  teamsById: Map<string, PlayEventTeam> = new Map()
): { sideA: T[]; sideB: T[] } {
  if (match.result === 'bye') {
    return { sideA: [], sideB: [] };
  }

  return {
    sideA: getPlayEventSidePlayerParticipants(
      match.sideAParticipantId,
      match.sideATeamId,
      participantsById,
      teamsById
    ),
    sideB: getPlayEventSidePlayerParticipants(
      match.sideBParticipantId,
      match.sideBTeamId,
      participantsById,
      teamsById
    ),
  };
}

export function getPlayEventMatchSideNames(
  match: Pick<
    PlayEventMatch,
    | 'result'
    | 'sideAParticipantId'
    | 'sideBParticipantId'
    | 'sideATeamId'
    | 'sideBTeamId'
  >,
  participantsById: Map<string, ParticipantNameLookup>,
  teamsById: Map<string, PlayEventTeam> = new Map()
): { sideA: string[]; sideB: string[] } {
  if (match.result === 'bye') {
    return { sideA: ['Bye'], sideB: [] };
  }

  return {
    sideA: getPlayEventSidePlayerNames(
      match.sideAParticipantId,
      match.sideATeamId,
      participantsById,
      teamsById
    ),
    sideB: getPlayEventSidePlayerNames(
      match.sideBParticipantId,
      match.sideBTeamId,
      participantsById,
      teamsById
    ),
  };
}

type PublicBoardRoundSchedule = Pick<
  PlayEvent,
  | 'matchupVisibilityMinutes'
  | 'startsAt'
  | 'endsAt'
  | 'roundCount'
  | 'roundDurationMinutes'
  | 'roundBreakMinutes'
>;

export interface ResolvePublicBoardRoundNumberOptions {
  now?: Date;
  event?: PublicBoardRoundSchedule | null;
  rounds?: Pick<PlayEventRound, 'roundNumber' | 'startsAt'>[] | null;
}

function roundHasUpcomingVisibleMatches(
  matches: Pick<PlayEventMatch, 'roundNumber' | 'result'>[],
  roundNumber: number
): boolean {
  return matches
    .filter((match) => match.roundNumber === roundNumber)
    .some((match) => match.result === 'unplayed' || match.result === 'bye');
}

/** Public boards show one round at a time — the latest round in its visibility window. */
export function resolvePublicBoardRoundNumber(
  matches: Pick<PlayEventMatch, 'roundNumber' | 'result'>[],
  options?: ResolvePublicBoardRoundNumberOptions
): number | null {
  if (matches.length === 0) return null;

  const roundNumbers = [...new Set(matches.map((match) => match.roundNumber))].sort(
    (a, b) => a - b
  );

  const event = options?.event;
  if (event?.roundCount && event.startsAt && event.endsAt) {
    const now = options?.now ?? new Date();
    const visibilityMs = event.matchupVisibilityMinutes * 60 * 1000;
    const roundsByNumber = new Map((options?.rounds ?? []).map((round) => [round.roundNumber, round]));
    let selectedRound: number | null = null;

    for (const roundNumber of roundNumbers) {
      if (!roundHasUpcomingVisibleMatches(matches, roundNumber)) continue;

      const roundStart = resolveRoundStartTime(
        roundsByNumber.get(roundNumber) ?? null,
        roundNumber,
        event
      );
      if (!roundStart) continue;

      if (now.getTime() >= roundStart.getTime() - visibilityMs) {
        selectedRound = roundNumber;
      }
    }

    if (selectedRound != null) return selectedRound;
  }

  for (const roundNumber of roundNumbers) {
    if (roundHasUpcomingVisibleMatches(matches, roundNumber)) {
      return roundNumber;
    }
  }

  return roundNumbers[roundNumbers.length - 1] ?? null;
}

export type RoundCourtTiming = 'upcoming' | 'on_court' | 'past';

export function resolveRoundCourtTiming(
  now: Date,
  roundStart: Date,
  roundDurationMinutes?: number | null
): RoundCourtTiming {
  const startMs = roundStart.getTime();
  if (now.getTime() < startMs) return 'upcoming';

  if (roundDurationMinutes && roundDurationMinutes > 0) {
    const endMs = startMs + roundDurationMinutes * 60 * 1000;
    if (now.getTime() >= endMs) return 'past';
  }

  return 'on_court';
}

export function formatPublicBoardRoundTimingLabel(
  roundStart: Date,
  now: Date,
  roundDurationMinutes?: number | null
): string {
  const timing = resolveRoundCourtTiming(now, roundStart, roundDurationMinutes);
  if (timing === 'upcoming') return `Starts ${format(roundStart, 'HH:mm')}`;
  if (timing === 'on_court') return 'On court';
  return 'Round ended';
}

export function formatPublicBoardRoundHeadingLabel(
  roundStart: Date,
  now: Date,
  roundDurationMinutes?: number | null
): string {
  const timing = resolveRoundCourtTiming(now, roundStart, roundDurationMinutes);
  return timing === 'upcoming' ? 'Next round' : 'Current round';
}

export function formatPublicBoardMatchStatusLabel(
  match: Pick<PlayEventMatch, 'result'>,
  now: Date,
  roundStart: Date,
  roundDurationMinutes?: number | null
): string {
  if (match.result !== 'unplayed' && match.result !== 'bye') {
    return match.result.replace(/_/g, ' ');
  }

  const timing = resolveRoundCourtTiming(now, roundStart, roundDurationMinutes);
  return timing === 'upcoming' ? 'On court soon' : 'On court';
}

export function isPlayEventBoardFinished(
  event: Pick<PlayEvent, 'status' | 'endsAt'>,
  now: Date = new Date()
): boolean {
  if (event.status === 'completed' || event.status === 'cancelled') return true;
  const endsAt = event.endsAt instanceof Date ? event.endsAt : new Date(event.endsAt);
  return now.getTime() >= endsAt.getTime();
}

export function shouldAutoCompletePlayEvent(
  event: Pick<PlayEvent, 'status' | 'endsAt'>,
  rounds: Pick<PlayEventRound, 'status'>[] = [],
  now: Date = new Date()
): boolean {
  if (event.status !== 'in_progress') return false;
  if (isPlayEventBoardFinished(event, now)) return true;
  return rounds.length > 0 && rounds.every((round) => round.status === 'completed');
}

export function isPlayEventBoardLive(
  event: Pick<PlayEvent, 'status' | 'startsAt' | 'endsAt'>,
  now: Date = new Date()
): boolean {
  if (event.status === 'cancelled') return false;
  if (isPlayEventBoardFinished(event, now)) return false;
  const startsAt = event.startsAt instanceof Date ? event.startsAt : new Date(event.startsAt);
  return now.getTime() >= startsAt.getTime();
}

export const PUBLIC_BOARD_EVENT_ENDING_WARNING_MINUTES = 5;

/** True during the final minutes before the event's scheduled end while it is still live. */
export function isPlayEventBoardEndingSoon(
  event: Pick<PlayEvent, 'status' | 'startsAt' | 'endsAt'>,
  now: Date = new Date(),
  warningMinutes: number = PUBLIC_BOARD_EVENT_ENDING_WARNING_MINUTES
): boolean {
  if (!isPlayEventBoardLive(event, now)) return false;

  const endsAt = event.endsAt instanceof Date ? event.endsAt : new Date(event.endsAt);
  const warningMs = warningMinutes * 60 * 1000;
  return now.getTime() >= endsAt.getTime() - warningMs;
}

export function formatPlayEventMatchup(
  match: Pick<
    PlayEventMatch,
    | 'result'
    | 'sideAParticipantId'
    | 'sideBParticipantId'
    | 'sideATeamId'
    | 'sideBTeamId'
  >,
  participantsById: Map<string, ParticipantNameLookup>,
  teamsById: Map<string, PlayEventTeam> = new Map()
): string {
  if (match.result === 'bye') return 'Bye';

  const { sideA, sideB } = getPlayEventMatchSideNames(match, participantsById, teamsById);
  const sideALabel = sideA.join(' & ');
  const sideBLabel = sideB.join(' & ');

  return `${sideALabel} vs ${sideBLabel}`;
}
